// src/pages/ProjectDetailPage.jsx
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, FolderOpen } from "lucide-react";
import { fetchWithAuth } from "../api/fetchWithAuth";
import ProjectMembersPanel from "./admin/ProjectMembersPanel";

/**
 * Per-project detail page. Mounted at /projects/$projectId via
 * projectDetailRoute in src/router.jsx.
 *
 * Props (supplied by router wrapper):
 *   projectId  — from URL
 *   navigate   — TanStack router navigate()
 *
 * Layout: project-metadata header + ProjectMembersPanel. When/if we
 * add Settings, AOIs, etc. they'd go below the header as additional
 * panels or get promoted to tabs.
 */
export default function ProjectDetailPage({ projectId, navigate }) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const res = await fetchWithAuth(
          `/api/v1/admin/projects/${projectId}`
        );

        if (cancelled) return;

        if (!res.ok) {
          // 403 is the "not your project / wrong org / you're not admin or
          // owner" catchall. 404 is genuinely missing. Either way the user
          // can't use this page, so we show the same error UI.
          if (res.status === 403) {
            setError("You don't have permission to view this project.");
          } else if (res.status === 404) {
            setError("Project not found.");
          } else {
            setError(`Could not load project (error ${res.status}).`);
          }
          return;
        }

        setProject(await res.json());
      } catch (e) {
        if (cancelled) return;
        console.error("[project-detail] load failed", e);
        setError("Network error loading project.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (projectId) load();
    return () => { cancelled = true; };
  }, [projectId]);

  if (loading) {
    return (
      <div className="flex justify-center pt-10">
        <Loader2 className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-3xl">
        <button
          onClick={() => navigate({ to: "/map/$tab", params: { tab: "projects" } })}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200 transition mb-4"
        >
          <ArrowLeft size={12} />
          Back to projects
        </button>
        <div className="text-red-400 text-sm">{error}</div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="p-6 max-w-3xl space-y-6">
      {/* Back link */}
      <button
        onClick={() => navigate({ to: "/map/$tab", params: { tab: "projects" } })}
        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200 transition"
      >
        <ArrowLeft size={12} />
        Back to projects
      </button>

      {/* Project header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <FolderOpen size={16} className="text-accent-info" />
          <h1 className="text-lg font-semibold text-gray-100">{project.name}</h1>
          <span
            className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wide ${
              project.status === "active"
                ? "bg-green-500/15 text-green-400"
                : "bg-slate-600/20 text-slate-400"
            }`}
          >
            {project.status}
          </span>
        </div>

        <div className="text-xs text-fg-secondary font-mono">{project.slug}</div>

        {project.description && (
          <p className="text-xs text-gray-400 leading-relaxed pt-1">
            {project.description}
          </p>
        )}
      </div>

      {/* Members — the actionable section. Passing no onMembersChange
          because this page doesn't currently have adjacent state that
          needs refresh. When we wire in a license/seat indicator on
          project-level seats we'll plumb a callback through. */}
      <div className="border-t border-slate-800 pt-5">
        <ProjectMembersPanel projectId={projectId} />
      </div>
    </div>
  );
}
